import type {DeletedDiff} from '@github-ui/code-view-types'
import {CopyToClipboardButton} from '@github-ui/copy-to-clipboard/Button'
import {DiffSquares} from '@github-ui/diffs/DiffParts'
import {IncludeFragment} from '@github-ui/include-fragment-react'
import {VerifiedHTMLBox} from '@github-ui/safe-html/VerifiedHTML'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton, Link, Spinner} from '@primer/react'
import {clsx} from 'clsx'
import {useState} from 'react'

import styles from './DeleteViewEntry.module.css'

export function DiffEntry({diff, index}: {diff: DeletedDiff; index: number}) {
  const [collapsed, setCollapsed] = useState(false)
  const [showDiff, setShowDiff] = useState(false)
  const [diffLoaded, setDiffLoaded] = useState(false)

  const squares: Array<'addition' | 'deletion' | 'neutral'> = []
  for (let i = 0; i < 5; i++) {
    if (diff.deletions > i) {
      squares.push('deletion')
    } else {
      squares.push('neutral')
    }
  }
  if (diff.diffHTML) {
    return (
      <div id="readme" className="readme prose-diff html-blob blob">
        <VerifiedHTMLBox html={diff.diffHTML} className="markdown-body container-lg" />
      </div>
    )
  }

  return (
    <div className={styles.diffEntry} id={`diff-entry-${index}`}>
      <div className={styles.diffHeader}>
        <IconButton
          aria-label={collapsed ? 'Expand diff' : 'Collapse diff'}
          icon={collapsed ? ChevronRightIcon : ChevronDownIcon}
          size="small"
          tooltipDirection="s"
          variant="invisible"
          onClick={() => setCollapsed(!collapsed)}
        />
        <span className="color-fg-muted">{diff.deletions}</span>
        <DiffSquares squares={squares} />
        <Link href={`#diff-entry-${index}`} className="color-fg-default">
          {diff.path}
        </Link>
        <CopyToClipboardButton textToCopy={diff.path} ariaLabel={'Copy path to clipboard'} />
      </div>
      {collapsed ? null : (
        <div className={showDiff ? styles.diffContentCollapsed : styles.diffContent} tabIndex={-1}>
          {showDiff ? (
            <IncludeFragment
              data-testid="delete-diff-fragment"
              src={diff.loadDiffPath}
              onLoad={() => setDiffLoaded(true)}
            >
              {!diffLoaded && (
                <div className={styles.loadingSpinner}>
                  <Spinner />
                </div>
              )}
            </IncludeFragment>
          ) : (
            <>
              <DiffPlaceholderSvg />
              <div className={styles.diffOverlay}>
                <Link as="button" onClick={() => setShowDiff(true)} inline>
                  Load diff
                </Link>
                This file was deleted.
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function DiffPlaceholderSvg() {
  return (
    <svg
      aria-hidden="true"
      className={clsx('width-full', styles.placeholderSvg)}
      viewBox="0 0 340 84"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        className={clsx('js-diff-placeholder', styles.placeholderPath)}
        clipPath="url(#diff-placeholder)"
        d="M0 0h340v84H0z"
        fillRule="evenodd"
      />
    </svg>
  )
}
